import React, { useCallback } from 'react';
import { Platform, ScrollView } from 'react-native';
import { StyleSheet, View } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import {
  BodyText,
  HStack,
  IllusCredit_2,
  Image,
  ShareIcon,
  theme,
} from '@sweetgreen/garnish';

import {
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenTitle,
  LoadingAnimation,
  NotFoundView,
} from '@order/components';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { useReferralShareUrlQuery } from './GraphQL/ReferFriend.generated';
import {
  EmailShareOption,
  FacebookShareOption,
  MoreShareOption,
  TwitterShareOption,
  WhatsappShareOption,
} from './shareOptions';

export const ReferFriendScreen = () => {
  const { t } = useLocalizationContext();
  const isFeatureEnabled = useFeatureFlag('permanent-refer-friend-enabled');

  if (!isFeatureEnabled) return <NotFoundView />;

  return (
    <AccountScreenContainer screen="refer-friend">
      <AccountScreenTitle title={t('account.refer-friend.title')} />
      <AccountScreenSection>
        <ReferFriendScreenContent />
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

export const ReferFriendScreenContent = () => {
  const { t } = useLocalizationContext();

  // ─── Query ───────────────────────────────────────────────────────────
  const [{ data }] = useReferralShareUrlQuery();
  const shareLink = data?.currentCustomer?.referralShareUrlEmail ?? '';

  // ─── Share Options ───────────────────────────────────────────────────
  const canShowMore = Boolean(Platform.OS !== 'web' || navigator.share);

  // ─── Copy Callback ───────────────────────────────────────────────────
  const handleCopyLink = useCallback(async () => {
    await Clipboard.setStringAsync(shareLink);
  }, [shareLink]);

  if (!shareLink) return <LoadingAnimation />;

  return (
    <View>
      {/* Image */}
      <View style={styles.imageContainer}>
        <Image
          style={styles.image}
          source={IllusCredit_2}
          accessibilityIgnoresInvertColors
        />
      </View>

      {/* Information */}
      <BodyText style={styles.titleMargin} size={2} bold>
        {t('account.refer-friend.subtitle')}
      </BodyText>
      <BodyText style={styles.sectionMargin}>
        {t('account.refer-friend.description')}
      </BodyText>

      {/* Share Icons Row */}
      <BodyText style={styles.titleMargin}>
        {t('account.refer-friend.share.hint')}
      </BodyText>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.sectionMargin}
      >
        <HStack>
          <EmailShareOption shareLink={shareLink} />
          <FacebookShareOption shareLink={shareLink} />
          <TwitterShareOption shareLink={shareLink} />
          <WhatsappShareOption shareLink={shareLink} />
          {canShowMore ? <MoreShareOption shareLink={shareLink} /> : null}
        </HStack>
      </ScrollView>

      {/* Copy Link Row */}
      <BodyText style={styles.titleMargin}>
        {t('account.refer-friend.copy.hint')}
      </BodyText>
      <View style={styles.copyRow}>
        <BodyText numberOfLines={1} style={styles.shareLink} size={4}>
          {shareLink}
        </BodyText>
        <ShareIcon
          style={styles.copyIconMargin}
          iconName="IconCopy"
          onShare={handleCopyLink}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  imageContainer: {
    marginBottom: theme.spacing.large,
    alignItems: 'center',
  },
  image: {
    width: 232,
    height: 200,
  },
  titleMargin: {
    marginBottom: theme.spacing.small,
  },
  sectionMargin: {
    marginBottom: theme.spacing.large + theme.spacing.medium,
  },
  shareLink: {
    lineHeight: 32, // to match copy icon size
  },
  copyRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.GRAY,
  },
  copyIconMargin: {
    marginLeft: theme.spacing.small,
  },
});
